import { defineElement, FC, onDidLoad, useEvent, useProp } from '@atomify/hooks';
import { getChildrenHeight } from '@source/utilities/get-children-height';
import { GTMPush } from '@utilities/gtm';
import { useMemoizedValue } from '@utilities/hooks/use-memoized-value';

const ARIA_EXPANDED = 'aria-expanded';
const ARIA_HIDDEN = 'aria-hidden';
const COLLAPSE_ACTIVE_CLASS = 'content-collapse--is-collapsed';

const DEFAULT_COLLAPSED_LABEL = 'Meer informatie';
const DEFAULT_EXPANDED_LABEL = 'Minder informatie';

export interface ContentCollapseElement extends HTMLElement {
    expanded: boolean;
}

const ContentCollapse: FC<ContentCollapseElement> = ({ element }) => {
    const [, setExpanded, watchExpanded] = useProp<boolean>('expanded', false);
    const [defaultHeight] = useProp<string>('defaultHeight', '0');
    const [name] = useProp<string>('name', '');
    const [gtmEvent] = useProp<string | null>('gtmEvent', null);
    const [gtmEventCategory] = useProp<string | null>('gtmEventCategory', null);
    const [gtmEventLabel] = useProp<string | null>('gtmEventLabel', null);

    const expandEvent = useEvent({ eventName: `contentCollapse[${name}]::expand` });
    const collapseEvent = useEvent({ eventName: `contentCollapse[${name}]::collapse` });
    let heightTransitionEvent: () => void;

    const relatedToggle = useMemoizedValue(() => {
        const toggle = document.querySelector<HTMLElement>(`[aria-controls="${element.id}"]`);
        toggle?.addEventListener('click', toggleExpanded);
        return toggle;
    });

    const labelContainer = useMemoizedValue(() =>
        relatedToggle()?.querySelector('[js-hook-content-collapse-label]'),
    );

    const collapsedLabel = useMemoizedValue(
        () => relatedToggle()?.dataset.collapsedLabel ?? DEFAULT_COLLAPSED_LABEL,
    );

    const expandedLabel = useMemoizedValue(
        () => relatedToggle()?.dataset.expandedLabel ?? DEFAULT_EXPANDED_LABEL,
    );

    const toggleExpanded = () => {
        setExpanded(!element.expanded);
    };

    onDidLoad(() => {
        relatedToggle();
        if (element.expanded) handleExpandedChanged(element.expanded);
    });

    watchExpanded((newExpanded) => {
        handleExpandedChanged(newExpanded);
    });

    function handleExpandedChanged(isExpanded: boolean) {
        setAriaAttributes(isExpanded);
        handleAnimation();

        if (!isExpanded) {
            element.classList.remove(COLLAPSE_ACTIVE_CLASS);
        }

        const newLabel = isExpanded ? expandedLabel() : collapsedLabel();

        if (isExpanded) {
            expandEvent.emit({});
            if (gtmEvent && gtmEventCategory && gtmEventLabel) {
                GTMPush({
                    event: gtmEvent,
                    eventCategory: gtmEventCategory,
                    eventLabel: gtmEventLabel,
                });
            }
        } else {
            collapseEvent.emit({});
        }

        const _labelContainer = labelContainer();
        const _relatedToggle = relatedToggle();
        if (_labelContainer) {
            // When using the useButtonComponent prop, there will be a labelContainer as well. Replace it's content instead of the entire button
            _labelContainer.innerHTML = newLabel;
        } else if (_relatedToggle) {
            _relatedToggle.innerHTML = newLabel;
        }
    }

    /**
     * Adds the correct aria attributes to the related button and the <content-collapse>.
     * @private
     * @param {boolean} state
     * @memberof ContentCollapse
     */
    function setAriaAttributes(state: boolean) {
        relatedToggle()?.setAttribute(ARIA_EXPANDED, `${state}`);
        element.setAttribute(ARIA_HIDDEN, `${!state}`);
    }

    /**
     * Applies the new calculated height to the <content-collapse>.
     * @private
     * @memberof ContentCollapse
     */
    function handleAnimation() {
        const childrenHeight = getChildrenHeight(element);

        if (childrenHeight) {
            heightTransitionEvent = () => heightTransitionEnd();
            setHeight(`${childrenHeight}px`);
            element.addEventListener('transitionend', heightTransitionEvent, false);
        }
    }

    /**
     * Sets the height based up on the expanded property.
     * @private
     * @param {string} height
     * @memberof ContentCollapse
     */
    function setHeight(height: string) {
        element.style.height = element.expanded ? defaultHeight : height;
        element.offsetHeight;
        element.style.height = element.expanded ? height : defaultHeight;
    }

    /**
     * Function that gets fired after the transition is ended and applies collapsed class.
     * @private
     * @memberof ContentCollapse
     */
    function heightTransitionEnd() {
        if (element.expanded) {
            element.style.height = 'auto';
            element.classList.add(COLLAPSE_ACTIVE_CLASS);
        }

        element.removeEventListener('transitionend', heightTransitionEvent, false);
    }
};

ContentCollapse.props = {
    expanded: {
        type: Boolean,
        reflectToAttr: true,
    },
    defaultHeight: {
        type: String,
    },
    name: {
        type: String,
        reflectToAttr: true,
    },
    gtmEvent: {
        type: String,
    },
    gtmEventCategory: {
        type: String,
    },
    gtmEventLabel: {
        type: String,
    },
};

defineElement('bpd-content-collapse', ContentCollapse);
